import { useEffect, useMemo, useRef, useState } from "react";
import SwaggerUIBundle from "swagger-ui-dist/swagger-ui-bundle";
import "swagger-ui-dist/swagger-ui.css";
import { API_SPECS } from "../docs";
import { useApp } from "../context/AppContext";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");

/**
 * Swagger UI over the OpenAPI documents bundled from docs/api/openapi/*.json:
 * the vision backend (generated from the functions by tools/docs/build_openapi.py),
 * the cabinet's local API, the weight service and the merchant-facing endpoints.
 * "Try it out" against the backend reuses the signed-in user's token and tenant.
 */
export function ApiDocsPage() {
  const { session, tenantId } = useApp();
  const token = session?.token || null;
  const [specId, setSpecId] = useState(API_SPECS[0]?.id);
  const host = useRef(null);
  const spec = useMemo(() => API_SPECS.find((s) => s.id === specId), [specId]);

  useEffect(() => {
    if (!host.current || !spec) return undefined;
    const doc = JSON.parse(JSON.stringify(spec.spec));
    if (spec.id === "vision-backend" && API_BASE) {
      doc.servers = [{ url: `${API_BASE}/api`, description: "this deployment" }, ...(doc.servers || [])];
    }
    const ui = SwaggerUIBundle({
      domNode: host.current,
      spec: doc,
      docExpansion: "none",
      defaultModelsExpandDepth: 0,
      deepLinking: false,
      tryItOutEnabled: false,
      displayRequestDuration: true,
      filter: true,
      requestInterceptor: (req) => {
        if (spec.id === "vision-backend") {
          if (token && !req.headers.Authorization) req.headers.Authorization = `Bearer ${token}`;
          if (tenantId && !req.headers["x-tenant-id"]) req.headers["x-tenant-id"] = tenantId;
        }
        return req;
      },
    });
    return () => {
      ui?.getSystem?.()?.specActions?.updateSpec?.("");
      if (host.current) host.current.innerHTML = "";
    };
  }, [spec, token, tenantId]);

  const ops = useMemo(() => {
    const paths = spec?.spec?.paths || {};
    return Object.values(paths).reduce((n, p) => n + Object.keys(p).length, 0);
  }, [spec]);

  if (!spec) {
    return (
      <div className="card">
        <h1 className="text-xl font-semibold">API reference</h1>
        <p className="text-sm text-slate-500">No OpenAPI documents are bundled in this build.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-semibold">API reference</h1>
          <p className="text-sm text-slate-500">
            {spec.title} · {Object.keys(spec.spec.paths || {}).length} paths, {ops} operations · version{" "}
            {spec.spec.info?.version}
          </p>
        </div>
        <label className="text-sm">
          <span className="label">API</span>
          <select className="input mt-1 w-auto" value={specId} onChange={(e) => setSpecId(e.target.value)}>
            {API_SPECS.map((s) => (
              <option key={s.id} value={s.id}>
                {s.title}
              </option>
            ))}
          </select>
        </label>
        <a
          className="btn-secondary"
          href={`data:application/json;charset=utf-8,${encodeURIComponent(JSON.stringify(spec.spec, null, 1))}`}
          download={`${spec.id}.openapi.json`}
        >
          Download OpenAPI
        </a>
      </div>
      <div className="card overflow-hidden p-0">
        <div ref={host} className="swagger-host" />
      </div>
    </div>
  );
}
